import { EventEmitter } from 'eventemitter3';

import { ContentCue } from './ContentCue';
import { ContentCueTrackEvent } from './ContentCueTrackEvent';
import { ContentTrackTypes } from './ContentTrackTypes';
import { IContentPlaylist } from './IContentPlaylist';

export class ContentCueTrack extends EventEmitter {
	public isInitialPhase: boolean = true;

	private readonly CONTENT_PLAYER_SPIN_UP_LEAD_SECS = 30; //time in seconds prior to the content start time to begin spinning up the player

	private _activeCues: ContentCue[];
	private _cues: ContentCue[];
	private _isActive: boolean;
	private _onTimeUpdateHandler: () => void;
	private _forcedResetActiveCues: boolean;

	constructor(public speakerHlsPlayer, contentPlaylists: Map<string, IContentPlaylist>, isActive: boolean) {
		super();

		this._activeCues = [];
		this._cues = [];
		this._isActive = isActive;
		this._onTimeUpdateHandler = () => this._updateActiveCues();

		//apply timeUpdated listener to video element
		this.speakerHlsPlayer.videoElement.addEventListener('timeupdate', this._onTimeUpdateHandler);

		//add content playlists
		contentPlaylists.forEach(contentPlaylist => this._addContentPlaylistCues(contentPlaylist));

		this._updateActiveCues();
	}

	public get activeCues(): ContentCue[] {
		return this._activeCues;
	}

	public get isNoActiveCues(): boolean {
		return this._activeCues.length > 0 && this._activeCues.every(cue => cue.isCompleted());
	}

	public getActivePlayCue(startSecs: number): ContentCue {
		return this._activeCues.find(cue => cue.type === ContentTrackTypes.PLAY && cue.contentPlaylist.start === startSecs);
	}

	public get currentDateTime(): Date {
		return this.speakerHlsPlayer.getCurrentProgramDateTime();
	}

	public get isActive(): boolean {
		return this._isActive;
	}

	public set isActive(isActive: boolean) {
		this._isActive = isActive;

		//reevaluate active cues
		this._updateActiveCues();
	}

	public addOrUpdateContentPlaylist(contentPlaylist: IContentPlaylist): void {
		const matchingPlayCue = this._cues.find(cue => cue.type === ContentTrackTypes.PLAY && cue.contentPlaylist.start === contentPlaylist.start);

		if (matchingPlayCue) { //update existing
			this._updateContentPlayCue(contentPlaylist, matchingPlayCue);
		} else { //add
			this._addContentPlaylistCues(contentPlaylist);
		}

		//reevaluate active cues
		this._updateActiveCues();
	}

	public destroy(): void {
		//remove event listener from speaker player
		if (this.speakerHlsPlayer && this.speakerHlsPlayer.videoElement) {
			this.speakerHlsPlayer.videoElement.removeEventListener('timeupdate', this._onTimeUpdateHandler);
		}
	}

	private get _initialProgramDateTime(): Date {
		return this.speakerHlsPlayer.initialProgramDateTime;
	}

	private _addContentPlaylistCues(contentPlaylist: IContentPlaylist): void {
		this._forcedResetActiveCues = false;
		//spin up cue
		const spinUpStart: number = Math.max(contentPlaylist.start - this.CONTENT_PLAYER_SPIN_UP_LEAD_SECS, 0); //either the configured lead time on the start OR 0 (prevent negatives)
		const spinUpEnd: number = contentPlaylist.start;
		const contentPlaylistStartDateTime: Date = new Date(+this._initialProgramDateTime + contentPlaylist.start * 1000);

		if (spinUpStart !== spinUpEnd) {
			this._cues.push(new ContentCue(this._initialProgramDateTime, contentPlaylist, ContentTrackTypes.SPIN_UP, spinUpStart, spinUpEnd, contentPlaylistStartDateTime, this.isInitialPhase));
		}

		//play cue
		this._cues.push(new ContentCue(this._initialProgramDateTime, contentPlaylist, ContentTrackTypes.PLAY, contentPlaylist.start, contentPlaylist.end, contentPlaylistStartDateTime, this.isInitialPhase));
	}

	private _updateContentPlayCue(contentPlaylist: IContentPlaylist, playCue: ContentCue): void {
		playCue.updateEndSecs(contentPlaylist.end);
	}

	private _updateActiveCues(): void {
		const currentDateTime: Date = this.currentDateTime;

		if (typeof currentDateTime === 'undefined' || this._forcedResetActiveCues) {
			return;
		}

		const isStopped: boolean = this.speakerHlsPlayer.isLive && this.speakerHlsPlayer.resumeHelper.isLivePaused;
		const activeCues: ContentCue[] = isStopped || !this.isActive ? [] : this._cues.filter(cue => cue.isActiveAt(currentDateTime));
		const isActiveCuesChanged: boolean = this._activeCues.length !== activeCues.length ||
			activeCues.reduce((out, current, currentIndex) => out || current !== this._activeCues[currentIndex], false);

		if (isActiveCuesChanged) {
			this.triggerCuesChangedEvent(activeCues);
		}
	}

	public resetActiveCues(): void {
		this._forcedResetActiveCues = true;
		this.triggerCuesChangedEvent([]);
	}

	public triggerCuesChangedEvent(activeCues: ContentCue[]): void {
		console.info('active cues changed', activeCues);
		this._activeCues = [...activeCues];

		//emit event
		this.emit(ContentCueTrackEvent.ACTIVE_CUES_CHANGED);
	}
}
